import React, { useEffect, useState } from 'react';
import { useCustomerJourney } from '../../context/setup';
import { User, Plus, CrownIcon, AlertCircle, Pencil, PencilIcon, Trash2Icon } from 'lucide-react';
import { Button } from '@/components/Button/ButtonV2';
import { useAuth } from '@/contexts';
import {
  Dialog,
  DialogContent,
  DialogContentHalfBottom,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/Dialog';
import { InputV2 } from '@/components/Input';
import { ActionType, BusinessRole, Professional } from '../../context/types';
import { emptySchedule } from '../../context/utils';
import { SupportedCountriesE } from '@/types/cyclone/models';
import { getPricesByCountry } from '@/utils/plans';
import { formatCurrencyWithLocale } from '@/utils/money';
import { Alert } from '@/components/Alert/Alert';
import { useWindowResize } from '@/hooks';
import { motion } from 'framer-motion';
import { trackGenericEvent } from '@/analytics';

const StaffCard = ({
  isAdmin,
  onEdit,
  onDelete,
  firstName,
  lastName
}: {
  isAdmin?: boolean;
  onEdit: () => void;
  onDelete?: () => void;
  firstName: string;
  lastName: string;
}) => {
  const getInitials = (firstName: string, lastName: string, fallbackName?: string) => {
    if (firstName && lastName) {
      return `${firstName[0] ?? ''}${lastName[0] ?? ''}`.toUpperCase();
    }
    if (firstName) {
      return firstName.substring(0, 2).toUpperCase();
    }
    if (fallbackName) {
      return fallbackName.substring(0, 2).toUpperCase();
    }
    return '';
  };

  // Handler to prevent click propagation from delete button
  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onDelete) onDelete();
  };

  return (
    <motion.div
      className={[
        'border rounded-xl bg-white shadow-sm transition-all overflow-hidden min-h-[80px] group',
        isAdmin ? 'border-yellow-300 cursor-default' : 'border-gray-200 cursor-pointer hover:bg-blue/5'
      ].join(' ')}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0 }}
      onClick={!isAdmin ? onEdit : undefined}
    >
      <div className="p-3">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div
              className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold text-sm ${
                isAdmin ? 'bg-yellow-500' : 'bg-blue'
              }`}
            >
              {getInitials(firstName, lastName)}
            </div>
            <div className="flex flex-col min-w-0 flex-1">
              <h3 className="font-medium text-sm truncate">
                {firstName} {lastName}
              </h3>
              <div className="flex items-center gap-2 mt-0.5">
                {isAdmin ? (
                  <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
                    <CrownIcon width={10} height={10} />
                    Admin
                  </span>
                ) : (
                  <span className="bg-blue/10 text-blue text-xs px-2 py-0.5 rounded-full">Pro</span>
                )}
              </div>
            </div>
          </div>

          {!isAdmin && (
            <div className="flex items-center gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
              <Button
                variant="ghost"
                size="sm"
                onClick={onEdit}
                className="text-gray-600 hover:text-gray-900 hover:bg-gray-200 p-1.5 h-auto"
              >
                <PencilIcon size={14} />
              </Button>
              {onDelete && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleDeleteClick}
                  className="text-red-500 hover:bg-red-100 hover:text-red-700 p-1.5 h-auto"
                >
                  <Trash2Icon size={14} />
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

const DialogAddProfessional = ({
  isOpen,
  setIsDialogOpen,
  selectedProfessional,
  staffPrice,
  firstName,
  setFirstName,
  lastName,
  setLastName,
  additionalProfessionals,
  handleSaveProfessional
}: {
  isOpen: boolean;
  setIsDialogOpen: (open: boolean) => void;
  selectedProfessional: Professional | null;
  firstName: string;
  staffPrice: number;
  setFirstName: (firstName: string) => void;
  lastName: string;
  setLastName: (lastName: string) => void;
  additionalProfessionals: number;
  handleSaveProfessional: () => void;
}) => {
  const { isMobileSize } = useWindowResize();
  const contentJSX = (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2 text-lg">
          {selectedProfessional ? (
            <>
              <Pencil className="h-5 w-5 text-blue" />
              Editar profesional
            </>
          ) : (
            <>
              <User className="h-5 w-5 text-blue" />
              Agregar nuevo profesional
            </>
          )}
        </DialogTitle>
      </DialogHeader>

      <div className="space-y-5 py-4">
        <div className="space-y-2">
          <label htmlFor="firstName" className="text-sm font-medium text-gray-700">
            Nombre
          </label>
          <InputV2
            id="firstName"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder="Ej: Juan"
          />
        </div>

        <div className="space-y-2">
          <label htmlFor="lastName" className="text-sm font-medium text-gray-700">
            Apellido
          </label>
          <InputV2
            id="lastName"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            placeholder="Ej: Pérez"
          />
        </div>

        {selectedProfessional === null && additionalProfessionals >= 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
            className="p-3 rounded-lg gap-3 flex items-start bg-yellow-50 border border-yellow-200"
          >
            <AlertCircle size={20} className="text-yellow-500 mt-0.5 flex-shrink-0" />
            <span className="text-gray-700 text-sm">
              Agregar un profesional adicional tendrá un costo extra de {formatCurrencyWithLocale(staffPrice)}{' '}
              por mes.
            </span>
          </motion.div>
        )}
      </div>

      <DialogFooter className="flex gap-3 mt-2">
        <Button variant="outline" onClick={() => setIsDialogOpen(false)} className="flex-1">
          Cancelar
        </Button>
        <Button
          onClick={handleSaveProfessional}
          disabled={!firstName || !lastName}
          className="bg-blue hover:bg-blue/90 flex-1"
        >
          {selectedProfessional ? 'Guardar' : 'Agregar'}
        </Button>
      </DialogFooter>
    </>
  );
  return (
    <Dialog open={isOpen} onOpenChange={setIsDialogOpen}>
      {isMobileSize ? (
        <DialogContentHalfBottom className="sm:max-w-md">{contentJSX}</DialogContentHalfBottom>
      ) : (
        <DialogContent className="sm:max-w-md">{contentJSX}</DialogContent>
      )}
    </Dialog>
  );
};

export const TeamCreationStep = () => {
  const { state, dispatch } = useCustomerJourney();
  const { session } = useAuth();
  const [totalPrice, setTotalPrice] = useState(0);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedProfessional, setSelectedProfessional] = useState<Professional | null>(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');

  const country = session?.vendor?.country || SupportedCountriesE.ARGENTINA;

  const prices = getPricesByCountry(country);

  const { venue_base_price, venue_staff_price } = prices;

  const additionalProfessionals = state.professionals.length - state.planProfessionalsCount;

  useEffect(() => {
    const basePrice =
      state.planProfessionalsCount === 2
        ? venue_base_price
        : venue_base_price + (state.planProfessionalsCount - 2) * venue_staff_price;
    const newprice =
      basePrice + (additionalProfessionals > 0 ? additionalProfessionals * venue_staff_price : 0);
    setTotalPrice(newprice);
  }, [
    venue_base_price,
    venue_staff_price,
    additionalProfessionals,
    state.professionals.length,
    state.planProfessionalsCount
  ]);

  useEffect(() => {
    if (session) {
      if (state.businessRole === BusinessRole.ADMIN_VENDOR) {
        const adminProfessional = state.professionals.find((p) => p.isAdmin);

        if (adminProfessional) {
          // Solo actualizar si los datos del session son diferentes a los actuales
          const needsUpdate =
            adminProfessional.first_name !== (session.first_name || 'Administrador') ||
            adminProfessional.last_name !== (session.last_name || '');

          if (needsUpdate) {
            dispatch({
              type: ActionType.UPDATE_PROFESSIONAL,
              payload: {
                ...adminProfessional,
                first_name: session.first_name || 'Administrador',
                last_name: session.last_name || '',
                hasWorkingHours: adminProfessional.hasWorkingHours,
                schedule: adminProfessional.schedule
              }
            });
          }
        } else if (state.professionals.length === 0) {
          // Solo crear profesional admin si no hay ninguno en el estado
          const newAdminProfessional: Professional = {
            id: 'pro-admin',
            cyclone_id: null,
            first_name: session.first_name || 'Administrador',
            last_name: session.last_name || '',
            isAdmin: true,
            hasWorkingHours: false,
            schedule: { ...emptySchedule }
          };

          dispatch({
            type: ActionType.ADD_PROFESSIONAL,
            payload: newAdminProfessional
          });
        }
      }
    }
  }, [session, dispatch, state.businessRole, state.professionals]);

  useEffect(() => {
    if (selectedProfessional) {
      setFirstName(selectedProfessional.first_name || '');
      setLastName(selectedProfessional.last_name || '');
    } else {
      setFirstName('');
      setLastName('');
    }
  }, [selectedProfessional, isDialogOpen]);

  const handleAddProfessional = () => {
    setSelectedProfessional(null);
    setIsDialogOpen(true);

    // Track professional addition start
    trackGenericEvent('Journey Team Add Professional Started');
  };

  const handleEditProfessional = (professional: Professional) => {
    setSelectedProfessional(professional);
    setIsDialogOpen(true);

    // Track professional edit start
    trackGenericEvent('Journey Team Edit Professional Started', {
      professionalId: professional.id,
      isAdmin: professional.isAdmin
    });
  };

  const handleDeleteProfessional = (professional: Professional) => {
    dispatch({
      type: ActionType.REMOVE_PROFESSIONAL,
      payload: professional.id
    });

    // Track professional deletion
    trackGenericEvent('Journey Team Professional Deleted', {
      professionalId: professional.id,
      totalProfessionals: state.professionals.length - 1
    });
  };

  const handleSaveProfessional = () => {
    if (selectedProfessional) {
      dispatch({
        type: ActionType.UPDATE_PROFESSIONAL,
        payload: {
          ...selectedProfessional,
          first_name: firstName,
          last_name: lastName
        }
      });

      // Track professional update
      trackGenericEvent('Journey Team Professional Updated', {
        professionalId: selectedProfessional.id,
        isAdmin: selectedProfessional.isAdmin
      });
    } else {
      dispatch({
        type: ActionType.ADD_PROFESSIONAL,
        payload: {
          id: `pro-${Date.now()}`,
          cyclone_id: null,
          first_name: firstName,
          last_name: lastName,
          hasWorkingHours: false,
          isAdmin: false,
          schedule: { ...emptySchedule }
        }
      });

      // Track professional addition
      trackGenericEvent('Journey Team Professional Added', {
        totalProfessionals: state.professionals.length + 1,
        additionalProfessionals: additionalProfessionals + 1
      });
    }

    setIsDialogOpen(false);
  };

  return (
    <>
      <div className="space-y-6 pb-4">
        <motion.div
          className="text-center mb-4"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <h1 className="text-2xl font-bold text-center">Tu Equipo de Profesionales</h1>
          <p className="text-gray-600 text-center mt-2">
            Configura los profesionales que brindan servicios en tu negocio
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {state.professionals.map((professional) => (
            <StaffCard
              key={professional.id}
              firstName={professional.first_name}
              lastName={professional.last_name}
              isAdmin={professional.isAdmin}
              onEdit={() => handleEditProfessional(professional)}
              {...(state.professionals.length > 2 && {
                onDelete: () => handleDeleteProfessional(professional)
              })}
            />
          ))}

          <motion.button
            className="border-2 border-blue border-dashed rounded-xl p-3 flex flex-col items-center justify-center cursor-pointer bg-white hover:bg-blue/5 transition-colors duration-200 min-h-[80px]"
            style={{ minHeight: '80px' }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3, delay: 0.2 }}
            onClick={handleAddProfessional}
          >
            <div className="w-8 h-8 border-2 border-blue rounded-full flex items-center justify-center mb-1 bg-blue/10">
              <Plus size={16} className="text-blue" />
            </div>
            <span className="font-medium text-blue text-center text-sm">Agregar profesional</span>
          </motion.button>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.3 }}
        >
          <Alert className="bg-blue/5 border-blue/20 rounded-xl mt-4 flex flex-col sm:flex-row sm:justify-between sm:items-center p-4 gap-2">
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-blue/10 rounded-full">
                <CrownIcon size={18} className="text-blue" />
              </div>
              <span className="font-medium text-gray-800">Total mensual:</span>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-sm text-gray-500">
                {state.professionals.length} profesional
                {state.professionals.length > 1 ? 'es' : ''}
              </span>
              <span className="text-xl font-bold text-blue">{formatCurrencyWithLocale(totalPrice)}</span>
            </div>
          </Alert>
        </motion.div>
      </div>

      <DialogAddProfessional
        isOpen={isDialogOpen}
        setIsDialogOpen={setIsDialogOpen}
        selectedProfessional={selectedProfessional}
        staffPrice={venue_staff_price}
        firstName={firstName}
        lastName={lastName}
        setFirstName={setFirstName}
        setLastName={setLastName}
        additionalProfessionals={additionalProfessionals}
        handleSaveProfessional={handleSaveProfessional}
      />
    </>
  );
};
